/**
 * TemplatesPage
 * Manage offer templates
 */

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/shared/lib/api";
import {
  Card,
  CardContent,
  Button,
  Input,
  Select,
  Badge,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from "@/shared/components/ui";
import { LabelWithTooltip } from "@/shared/components/ui/Tooltip";
import { TOOLTIPS } from "@/shared/lib/tooltips";
import { getCreditTypeLabel } from "@/shared/lib/utils";
import {
  Plus,
  ClipboardList,
  Star,
  Pencil,
  Trash2,
  Loader2,
  AlertCircle,
  CheckCircle,
} from "lucide-react";
import type { OfferTemplate, CreditType, CreateTemplateForm } from "@/shared/types";

const CREDIT_TYPES: { value: CreditType; label: string }[] = [
  { value: "hipoteczny", label: "Hipoteczny" },
  { value: "gotowkowy", label: "Gotówkowy" },
  { value: "konsolidacyjny", label: "Konsolidacyjny" },
];

const initialForm: CreateTemplateForm = {
  name: "",
  bankName: "",
  productName: "",
  type: "hipoteczny",
  defaultPeriod: 360,
  defaultInterestRate: 7.5,
  defaultRrso: 8.0,
  defaultCommission: 0,
  defaultMargin: 2.0,
  notes: "",
  isDefault: false,
};

export function TemplatesPage() {
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<OfferTemplate | null>(null);
  const [deletingTemplate, setDeletingTemplate] = useState<OfferTemplate | null>(null);
  const [form, setForm] = useState<CreateTemplateForm>(initialForm);

  const { data, isLoading, error } = useQuery({
    queryKey: ["templates"],
    queryFn: () => api.getTemplates(),
  });

  const templates = (data?.templates || []) as OfferTemplate[];

  // Sort templates: default first, then by usage count
  const sortedTemplates = [...templates].sort((a, b) => {
    if (a.isDefault && !b.isDefault) return -1;
    if (!a.isDefault && b.isDefault) return 1;
    return b.usageCount - a.usageCount;
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateTemplateForm) => api.createTemplate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
      setIsDialogOpen(false);
      setForm(initialForm);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateTemplateForm> }) =>
      api.updateTemplate(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
      setIsDialogOpen(false);
      setEditingTemplate(null);
      setForm(initialForm);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
      setIsDeleteDialogOpen(false);
      setDeletingTemplate(null);
    },
  });

  const handleOpenCreate = () => {
    setEditingTemplate(null);
    setForm(initialForm);
    setIsDialogOpen(true);
  };

  const handleOpenEdit = (template: OfferTemplate) => {
    setEditingTemplate(template);
    setForm({
      name: template.name,
      bankName: template.bankName,
      productName: template.productName,
      type: template.type,
      defaultPeriod: template.defaultPeriod,
      defaultInterestRate: template.defaultInterestRate,
      defaultRrso: template.defaultRrso,
      defaultCommission: template.defaultCommission,
      defaultMargin: template.defaultMargin,
      notes: template.notes || "",
      isDefault: template.isDefault || false,
    });
    setIsDialogOpen(true);
  };

  const handleOpenDelete = (template: OfferTemplate) => {
    setDeletingTemplate(template);
    setIsDeleteDialogOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingTemplate) {
      updateMutation.mutate({ id: editingTemplate.id, data: form });
    } else {
      createMutation.mutate(form);
    }
  };

  const handleConfirmDelete = () => {
    if (deletingTemplate) {
      deleteMutation.mutate(deletingTemplate.id);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;
  const mutationError = createMutation.error || updateMutation.error;

  if (error) {
    return (
      <div className="p-6 text-center">
        <AlertCircle className="w-12 h-12 text-error-500 mx-auto mb-4" />
        <p className="text-gray-600">Nie udało się pobrać szablonów</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Szablony ofert</h1>
          <p className="text-gray-600 mt-1">
            Twórz szablony, aby szybciej generować oferty
          </p>
        </div>
        <Button onClick={handleOpenCreate} leftIcon={<Plus className="w-4 h-4" />}>
          Dodaj szablon
        </Button>
      </div>

      {/* Templates List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      ) : templates.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <ClipboardList className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600 mb-2">Brak szablonów ofert</p>
            <p className="text-sm text-gray-500 mb-4">
              Utwórz szablony, aby przyspieszyć tworzenie ofert
            </p>
            <Button onClick={handleOpenCreate} leftIcon={<Plus className="w-4 h-4" />}>
              Utwórz pierwszy szablon
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {sortedTemplates.map((template) => (
            <Card key={template.id} className="hover:shadow-sm transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-gray-900 truncate">
                        {template.name}
                      </h3>
                      {template.isDefault && (
                        <span className="flex items-center gap-1 text-xs text-warning-600 bg-warning-50 px-2 py-0.5 rounded-full">
                          <Star className="w-3 h-3" fill="currentColor" />
                          Domyślny
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
                      <Badge variant="default">
                        {getCreditTypeLabel(template.type)}
                      </Badge>
                      {template.defaultRrso && (
                        <span>RRSO ~{template.defaultRrso}%</span>
                      )}
                      {template.defaultMargin && (
                        <span>Marża {template.defaultMargin}%</span>
                      )}
                      {template.defaultCommission !== undefined && (
                        <span>Prowizja {template.defaultCommission}%</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      Bank: {template.bankName} | Produkt: {template.productName} |
                      Użyty: {template.usageCount} {template.usageCount === 1 ? "raz" : "razy"}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleOpenEdit(template)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleOpenDelete(template)}
                      className="text-gray-500 hover:text-error-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate ? "Edytuj szablon" : "Nowy szablon oferty"}
            </DialogTitle>
            <DialogDescription>
              {editingTemplate
                ? "Zaktualizuj dane szablonu oferty"
                : "Utwórz szablon do szybkiego generowania ofert"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <DialogBody>
              <div className="space-y-4">
                <Input
                  label="Nazwa szablonu"
                  placeholder="np. PKO BP Hipoteczny Standard"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />

                <div className="grid grid-cols-2 gap-3">
                  <Input
                    label="Nazwa banku"
                    placeholder="np. PKO BP"
                    value={form.bankName}
                    onChange={(e) => setForm({ ...form, bankName: e.target.value })}
                    required
                  />
                  <Input
                    label="Nazwa produktu"
                    placeholder="np. Własny Kąt"
                    value={form.productName}
                    onChange={(e) => setForm({ ...form, productName: e.target.value })}
                    required
                  />
                </div>

                <Select
                  label="Typ kredytu"
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value as CreditType })}
                  options={CREDIT_TYPES}
                />

                <div className="grid grid-cols-2 gap-3">
                  <Input
                    label="Okres (miesiące)"
                    type="number"
                    min={1}
                    max={420}
                    value={form.defaultPeriod}
                    onChange={(e) =>
                      setForm({ ...form, defaultPeriod: parseInt(e.target.value) || 0 })
                    }
                    required
                  />
                  <Input
                    label={
                      <LabelWithTooltip
                        label="Oprocentowanie (%)"
                        tooltip={TOOLTIPS.interestRate}
                      />
                    }
                    type="number"
                    step="0.01"
                    min={0}
                    value={form.defaultInterestRate}
                    onChange={(e) =>
                      setForm({ ...form, defaultInterestRate: parseFloat(e.target.value) || 0 })
                    }
                    required
                  />
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <Input
                    label={
                      <LabelWithTooltip label="RRSO (%)" tooltip={TOOLTIPS.rrso} />
                    }
                    type="number"
                    step="0.01"
                    min={0}
                    value={form.defaultRrso || ""}
                    onChange={(e) =>
                      setForm({ ...form, defaultRrso: parseFloat(e.target.value) || undefined })
                    }
                  />
                  <Input
                    label={
                      <LabelWithTooltip label="Marża (%)" tooltip={TOOLTIPS.margin} />
                    }
                    type="number"
                    step="0.01"
                    min={0}
                    value={form.defaultMargin || ""}
                    onChange={(e) =>
                      setForm({ ...form, defaultMargin: parseFloat(e.target.value) || undefined })
                    }
                  />
                  <Input
                    label={
                      <LabelWithTooltip label="Prowizja (%)" tooltip={TOOLTIPS.commission} />
                    }
                    type="number"
                    step="0.01"
                    min={0}
                    value={form.defaultCommission || ""}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        defaultCommission: parseFloat(e.target.value) || undefined,
                      })
                    }
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Domyślne uwagi (opcjonalne)
                  </label>
                  <textarea
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    placeholder="Uwagi, które będą automatycznie dodawane do ofert..."
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.isDefault}
                    onChange={(e) => setForm({ ...form, isDefault: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">
                    Ustaw jako domyślny dla typu{" "}
                    <strong>{getCreditTypeLabel(form.type)}</strong>
                  </span>
                </label>

                {mutationError && (
                  <div className="p-3 bg-error-50 border border-error-200 rounded-lg flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-error-600 flex-shrink-0" />
                    <p className="text-sm text-error-700">
                      {(mutationError as Error).message}
                    </p>
                  </div>
                )}
              </div>
            </DialogBody>
            <DialogFooter>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setIsDialogOpen(false)}
                disabled={isPending}
              >
                Anuluj
              </Button>
              <Button
                type="submit"
                isLoading={isPending}
                leftIcon={<CheckCircle className="w-4 h-4" />}
              >
                {editingTemplate ? "Zapisz zmiany" : "Utwórz szablon"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Usuń szablon</DialogTitle>
            <DialogDescription>
              Czy na pewno chcesz usunąć szablon "{deletingTemplate?.name}"?
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            <p className="text-sm text-gray-600">
              Ta operacja jest nieodwracalna. Szablon zostanie trwale usunięty.
            </p>

            {deleteMutation.error && (
              <div className="mt-4 p-3 bg-error-50 border border-error-200 rounded-lg flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-error-600 flex-shrink-0" />
                <p className="text-sm text-error-700">
                  {(deleteMutation.error as Error).message}
                </p>
              </div>
            )}
          </DialogBody>
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => setIsDeleteDialogOpen(false)}
              disabled={deleteMutation.isPending}
            >
              Anuluj
            </Button>
            <Button
              variant="danger"
              onClick={handleConfirmDelete}
              isLoading={deleteMutation.isPending}
              leftIcon={<Trash2 className="w-4 h-4" />}
            >
              Usuń szablon
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
